import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, withModifiers as _withModifiers, createTextVNode as _createTextVNode, vShow as _vShow, withDirectives as _withDirectives, createBlock as _createBlock, Teleport as _Teleport } from "vue"

const _hoisted_1 = { class: "pr-6" }
const _hoisted_2 = {
  key: 0,
  class: "space-y-3"
}
const _hoisted_3 = { class: "grid grid-cols-3 gap-3" }
const _hoisted_4 = { class: "mb-5 grid grid-cols-3 gap-3" }
const _hoisted_5 = { class: "rounded-xl bg-orange-50 px-3 py-3" }
const _hoisted_6 = { class: "mt-1 text-2xl font-semibold leading-none text-black" }
const _hoisted_7 = { class: "rounded-xl bg-gray-50 px-3 py-3" }
const _hoisted_8 = { class: "mt-1 text-2xl font-semibold leading-none text-black" }
const _hoisted_9 = { class: "rounded-xl bg-gray-50 px-3 py-3" }
const _hoisted_10 = { class: "mt-1 text-2xl font-semibold leading-none text-black" }
const _hoisted_11 = {
  key: 0,
  class: "mb-4 flex items-center gap-2"
}
const _hoisted_12 = {
  key: 1,
  class: "mb-3 text-xs text-gray-500"
}
const _hoisted_13 = {
  key: 2,
  class: "rounded-xl bg-gray-50 px-4 py-10 text-center"
}
const _hoisted_14 = {
  key: 3,
  class: "space-y-3"
}
const _hoisted_15 = ["onClick"]
const _hoisted_16 = { class: "min-w-0 flex-1 truncate text-sm font-semibold text-black" }
const _hoisted_17 = { class: "shrink-0 text-xs text-gray-500" }
const _hoisted_18 = { class: "shrink-0 text-sm font-semibold text-black" }
const _hoisted_19 = ["onClick"]
const _hoisted_20 = ["src", "alt", "onError"]
const _hoisted_21 = {
  key: 1,
  class: "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-xs font-semibold text-gray-400"
}
const _hoisted_22 = { class: "min-w-0 flex-1" }
const _hoisted_23 = { key: 0 }
const _hoisted_24 = { class: "flex max-h-[90vh] max-w-[95vw] flex-col items-center gap-3" }
const _hoisted_25 = ["src", "alt"]
const _hoisted_26 = { class: "text-sm text-white" }
const _hoisted_27 = { key: 0 }
const _hoisted_28 = { key: 1 }

import { computed, onUnmounted, reactive, ref, watch } from 'vue'
import UnifiedPagination from '@/components/ui/UnifiedPagination.vue'
import { getProductPhotos } from '@/api/shooting'
import type { PanierAcheteurRow, ProduitDansPanier } from '@/types'

const perPage = 10

export default /*@__PURE__*/_defineComponent({
  __name: 'CartProductsSection',
  props: {
    paniers: { default: () => [] },
    loading: { type: Boolean, default: false },
    acheteurs: { default: 0 },
    montant: { default: 0 },
    commandeMini: { default: 0 }
  },
  setup(__props: any) {

const props = __props

const page = ref(1)
const openIds = ref<number[]>([])
const sortBy = ref<'date' | 'prix'>('date')

const sortedPaniers = computed(() => {
  const rows = [...props.paniers]
  if (sortBy.value === 'prix') {
    return rows.sort((a, b) => b.montant - a.montant)
  }
  return rows.sort((a, b) => cartTime(b.date) - cartTime(a.date))
})

const pagePaniers = computed(() => {
  const start = (page.value - 1) * perPage
  return sortedPaniers.value.slice(start, start + perPage)
})

const hiddenPaniers = computed(() =>
  Math.max(0, props.acheteurs - props.paniers.length),
)

watch(() => props.paniers, () => {
  page.value = 1
  openIds.value = []
})

watch(page, () => {
  openIds.value = []
})

watch(sortBy, () => {
  page.value = 1
  openIds.value = []
})

const cartTime = (value?: string | null) => {
  if (!value) return 0
  const time = new Date(value).getTime()
  return Number.isFinite(time) ? time : 0
}

const formatDate = (value?: string | null) => {
  const time = cartTime(value)
  if (!time) return ''
  const date = new Date(time)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${day}/${month}/${date.getFullYear()}`
}

const panierLabel = (panier: PanierAcheteurRow) => {
  const date = formatDate(panier.date)
  const title = date ? `Panier du ${date}` : 'Panier'
  return panier.pays ? `${title} · ${panier.pays}` : title
}

const isOpen = (id: number) => openIds.value.includes(id)

const togglePanier = (id: number) => {
  openIds.value = isOpen(id)
    ? openIds.value.filter((openId) => openId !== id)
    : [...openIds.value, id]
}

const failedImages = reactive<Record<number, boolean>>({})
const galleryOpen = ref(false)
const galleryLoading = ref(false)
const galleryPhotos = ref<string[]>([])
const galleryIndex = ref(0)
const galleryTitle = ref('')

const currentGalleryPhoto = computed(() => galleryPhotos.value[galleryIndex.value] ?? '')

const closePhotos = () => {
  galleryOpen.value = false
  galleryPhotos.value = []
  galleryIndex.value = 0
}

const showPreviousPhoto = () => {
  if (galleryPhotos.value.length < 2) return
  galleryIndex.value = (galleryIndex.value - 1 + galleryPhotos.value.length) % galleryPhotos.value.length
}

const showNextPhoto = () => {
  if (galleryPhotos.value.length < 2) return
  galleryIndex.value = (galleryIndex.value + 1) % galleryPhotos.value.length
}

const onGalleryKey = (event: KeyboardEvent) => {
  if (!galleryOpen.value) return
  if (event.key === 'Escape') closePhotos()
  if (event.key === 'ArrowLeft') showPreviousPhoto()
  if (event.key === 'ArrowRight') showNextPhoto()
}

window.addEventListener('keydown', onGalleryKey)
onUnmounted(() => window.removeEventListener('keydown', onGalleryKey))

const openPhotos = async (produit: ProduitDansPanier) => {
  if (!produit.imageUrl) return
  galleryTitle.value = produit.reference
  galleryPhotos.value = [produit.imageUrl]
  galleryIndex.value = 0
  galleryOpen.value = true
  galleryLoading.value = true
  try {
    const data = await getProductPhotos(produit.id, 'fiche')
    const photos = (data.photos ?? []).filter((url) => Boolean(url))
    if (photos.length > 0) galleryPhotos.value = photos
  } catch {
    galleryPhotos.value = [produit.imageUrl]
  } finally {
    galleryLoading.value = false
  }
}

const formatCurrency = (value: number) =>
  `${Number(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`

const getInitial = (reference: string) =>
  (reference || '?').trim().charAt(0).toUpperCase() || '?'

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_ctx.loading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(3, (index) => {
              return _createElementVNode("div", {
                key: index,
                class: "h-16 animate-pulse rounded-xl bg-gray-100"
              })
            }), 64))
          ]),
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(3, (index) => {
            return _createElementVNode("div", {
              key: `row-${index}`,
              class: "h-24 animate-pulse rounded-xl bg-gray-50"
            })
          }), 64))
        ]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _cache[3] || (_cache[3] = _createElementVNode("p", { class: "text-xs font-medium text-orange-800" }, "Acheteurs", -1)),
              _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.acheteurs), 1)
            ]),
            _createElementVNode("div", _hoisted_7, [
              _cache[4] || (_cache[4] = _createElementVNode("p", { class: "text-xs font-medium text-gray-500" }, "Minimum de notre boutique", -1)),
              _createElementVNode("p", _hoisted_8, _toDisplayString(_ctx.commandeMini > 0 ? formatCurrency(_ctx.commandeMini) : '—'), 1)
            ]),
            _createElementVNode("div", _hoisted_9, [
              _cache[5] || (_cache[5] = _createElementVNode("p", { class: "text-xs font-medium text-gray-500" }, "Montant des paniers", -1)),
              _createElementVNode("p", _hoisted_10, _toDisplayString(formatCurrency(_ctx.montant)), 1)
            ])
          ]),
          (_ctx.paniers.length > 1)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-xs font-medium text-gray-500" }, "Trier par", -1)),
                _createElementVNode("button", {
                  type: "button",
                  class: _normalizeClass(["rounded-full px-3 py-1 text-xs font-medium", sortBy.value === 'date' ? 'bg-black text-white' : 'bg-gray-100 text-black']),
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (sortBy.value = 'date'))
                }, " Date ", 2),
                _createElementVNode("button", {
                  type: "button",
                  class: _normalizeClass(["rounded-full px-3 py-1 text-xs font-medium", sortBy.value === 'prix' ? 'bg-black text-white' : 'bg-gray-100 text-black']),
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (sortBy.value = 'prix'))
                }, " Prix ", 2)
              ]))
            : _createCommentVNode("", true),
          (hiddenPaniers.value > 0)
            ? (_openBlock(), _createElementBlock("p", _hoisted_12, _toDisplayString(_ctx.paniers.length) + " panier" + _toDisplayString(_ctx.paniers.length > 1 ? 's' : '') + " affiché" + _toDisplayString(_ctx.paniers.length > 1 ? 's' : '') + " sur " + _toDisplayString(_ctx.acheteurs) + ". ", 1))
            : _createCommentVNode("", true),
          (_ctx.paniers.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_13, _cache[7] || (_cache[7] = [
                _createElementVNode("i", { class: "fas fa-shopping-basket text-2xl text-gray-300" }, null, -1),
                _createElementVNode("p", { class: "mt-3 text-sm text-gray-500" }, "Aucun panier en cours pour le moment.", -1)
              ])))
            : (_openBlock(), _createElementBlock("div", _hoisted_14, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pagePaniers.value, (panier) => {
                  return (_openBlock(), _createElementBlock("article", {
                    key: panier.id,
                    class: "overflow-hidden rounded-xl border border-gray-100"
                  }, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "flex w-full items-center gap-3 bg-gray-50 px-3 py-2.5 text-left",
                      onClick: ($event: any) => (togglePanier(panier.id))
                    }, [
                      _createElementVNode("i", {
                        class: _normalizeClass(["fas fa-chevron-right shrink-0 text-xs text-gray-400 transition-transform", isOpen(panier.id) ? 'rotate-90' : ''])
                      }, null, 2),
                      _createElementVNode("span", _hoisted_16, _toDisplayString(panierLabel(panier)), 1),
                      _createElementVNode("span", _hoisted_17, _toDisplayString(panier.produits.length) + " produit" + _toDisplayString(panier.produits.length > 1 ? 's' : ''), 1),
                      _createElementVNode("span", _hoisted_18, _toDisplayString(formatCurrency(panier.montant)), 1)
                    ], 8, _hoisted_15),
                    _withDirectives(_createElementVNode("ul", null, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(panier.produits, (produit) => {
                        return (_openBlock(), _createElementBlock("li", {
                          key: produit.id,
                          class: "flex items-center gap-3 border-t border-gray-100 px-3 py-2"
                        }, [
                          (produit.imageUrl && !failedImages[produit.id])
                            ? (_openBlock(), _createElementBlock("button", {
                                key: 0,
                                type: "button",
                                class: "h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-gray-100 cursor-zoom-in",
                                title: "Voir les photos",
                                onClick: _withModifiers(($event: any) => (openPhotos(produit)), ["stop"])
                              }, [
                                _createElementVNode("img", {
                                  src: produit.imageUrl,
                                  alt: produit.reference,
                                  class: "h-full w-full object-cover",
                                  onError: ($event: any) => (failedImages[produit.id] = true)
                                }, null, 40, _hoisted_20)
                              ], 8, _hoisted_19))
                            : (_openBlock(), _createElementBlock("div", _hoisted_21, _toDisplayString(getInitial(produit.reference)), 1)),
                          _createElementVNode("div", _hoisted_22, [
                            _createElementVNode("p", {
                              class: _normalizeClass(["truncate text-sm", produit.rupture ? 'font-medium text-red-600' : 'text-black'])
                            }, [
                              _createTextVNode(_toDisplayString(produit.reference) + " ", 1),
                              (produit.rupture)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_23, " · Rupture"))
                                : _createCommentVNode("", true)
                            ], 2),
                            (produit.categorie)
                              ? (_openBlock(), _createElementBlock("p", {
                                  key: 0,
                                  class: _normalizeClass(["truncate text-xs", produit.rupture ? 'text-red-500' : 'text-gray-500'])
                                }, _toDisplayString(produit.categorie), 3))
                              : _createCommentVNode("", true)
                          ]),
                          _createElementVNode("p", {
                            class: _normalizeClass(["shrink-0 text-xs font-medium", produit.rupture ? 'text-red-600' : 'text-gray-600'])
                          }, _toDisplayString(produit.quantite) + " pcs ", 3)
                        ]))
                      }), 128))
                    ], 512), [
                      [_vShow, isOpen(panier.id)]
                    ])
                  ]))
                }), 128))
              ])),
          (_ctx.paniers.length > perPage)
            ? (_openBlock(), _createBlock(UnifiedPagination, {
                key: 4,
                class: "!mt-4 !mb-0",
                "current-page": page.value,
                "total-items": _ctx.paniers.length,
                "items-per-page": perPage,
                "item-label": "paniers",
                "show-items-per-page": false,
                "show-first-last-buttons": true,
                "onUpdate:currentPage": _cache[2] || (_cache[2] = ($event: any) => (page.value = $event))
              }, null, 8, ["current-page", "total-items"]))
            : _createCommentVNode("", true)
        ], 64)),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (galleryOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "fixed inset-0 z-[10050] flex items-center justify-center bg-black/80 p-4",
            onClick: _withModifiers(closePhotos, ["self"])
          }, [
            _createElementVNode("button", {
              type: "button",
              class: "absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20",
              "aria-label": "Fermer",
              onClick: closePhotos
            }, _cache[8] || (_cache[8] = [
              _createElementVNode("i", { class: "fas fa-times" }, null, -1)
            ])),
            (galleryPhotos.value.length > 1)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  class: "absolute left-4 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20",
                  "aria-label": "Photo précédente",
                  onClick: _withModifiers(showPreviousPhoto, ["stop"])
                }, _cache[9] || (_cache[9] = [
                  _createElementVNode("i", { class: "fas fa-chevron-left" }, null, -1)
                ])))
              : _createCommentVNode("", true),
            (galleryPhotos.value.length > 1)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  type: "button",
                  class: "absolute right-4 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20",
                  "aria-label": "Photo suivante",
                  onClick: _withModifiers(showNextPhoto, ["stop"])
                }, _cache[10] || (_cache[10] = [
                  _createElementVNode("i", { class: "fas fa-chevron-right" }, null, -1)
                ])))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_24, [
              (currentGalleryPhoto.value)
                ? (_openBlock(), _createElementBlock("img", {
                    key: 0,
                    src: currentGalleryPhoto.value,
                    alt: galleryTitle.value,
                    class: "max-h-[80vh] max-w-[95vw] rounded-2xl object-contain shadow-2xl"
                  }, null, 8, _hoisted_25))
                : _createCommentVNode("", true),
              _createElementVNode("p", _hoisted_26, [
                _createTextVNode(_toDisplayString(galleryTitle.value) + " ", 1),
                (galleryPhotos.value.length > 1)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_27, " · " + _toDisplayString(galleryIndex.value + 1) + " / " + _toDisplayString(galleryPhotos.value.length), 1))
                  : _createCommentVNode("", true),
                (galleryLoading.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_28, " · chargement…"))
                  : _createCommentVNode("", true)
              ])
            ])
          ]))
        : _createCommentVNode("", true)
    ]))
  ]))
}
}

})